import React, { FunctionComponent, useState } from "react";
import { Box } from "@mui/material";
import { formatDateTime } from "src/utils/date/dateHelper";
import { McGeneralDiseaseItem, mcGeneralDiseaseService } from "src/api/medcards/mcGeneralDiseaseService";
import { useTranslate } from "src/locales";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

const dateIcon = "/assets/icons/clarity_date-line.svg";
const noteIcon = "/assets/icons/material-symbols_note-outline.svg";
const editIcon = "/assets/icons/mdi_pencil-outline.svg";
const trashIcon = "/assets/icons/mynaui_trash.svg";

interface SurgeryCardProps {
  name: string;
  operationDate: string;
  operator: string;
  note: string;
  surgeryDate: string;
  institution: string;
  id: number;
  originalItem: McGeneralDiseaseItem;
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const InfoRow: FunctionComponent<{ icon: string; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      alignSelf: "stretch",
      borderTop: "1px solid rgba(199, 199, 199, 0.3)",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "6px 16px",
    }}
  >
    <Box
      sx={{
        alignSelf: "stretch",
        height: "32px",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "8px 0px",
        boxSizing: "border-box",
        gap: "8px",
      }}
    >
      <Box component="img" src={icon} sx={{ height: "16px", width: "16px", position: "relative" }} alt="" />
      <Box sx={{ width: "110px", position: "relative", fontWeight: 600, display: "inline-block", flexShrink: 0 }}>
        {label}
      </Box>
      <Box sx={{ position: "relative", fontWeight: 300 }}>{value}</Box>
    </Box>
  </Box>
);

const SurgeryCard: FunctionComponent<SurgeryCardProps> = ({
  name,
  operationDate,
  operator,
  note,
  surgeryDate,
  institution,
  id,
  originalItem,
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await mcGeneralDiseaseService.deleteGeneralDisease(id);
      onDelete?.(id);
      onDeleteSuccess?.();
      return true;
    } catch (error) {
      console.error("Error deleting surgery:", error);
      onDeleteError?.();
      return false;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "318px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: "12.01px",
          color: "#101010",
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "318px",
            boxShadow:
              "0px 4.0019307136535645px 7px -5px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(199, 199, 199, 0.25)",
            borderRadius: "8px",
            backgroundColor: "#fff",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              backgroundColor: "#f9f9f9",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "8px 16px",
              fontSize: "16.01px",
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                minHeight: "48px",
                height: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "286px",
                  overflow: "hidden",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0px",
                  boxSizing: "border-box",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    width: "100%",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4,
                      maxHeight: "2.8em",
                    }}
                  >
                    {name}
                  </Box>
                  <Box
                    component="img"
                    src={trashIcon}
                    alt={t("prev-surgery.delete")}
                    onClick={handleDeleteClick}
                    sx={{ width: "24px", position: "relative", maxHeight: "100%", cursor: "pointer" }}
                  />
                  <Box
                    component="img"
                    src={editIcon}
                    alt={t("prev-surgery.edit")}
                    onClick={() =>
                      onEdit?.(originalItem)
                    }
                    sx={{ width: "24px", height: "24px", marginLeft: "8px", position: "relative", cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <InfoRow icon={dateIcon} label={t("prev-surgery.operation-date")} value={operationDate} />
        <InfoRow icon={dateIcon} label={t("prev-surgery.operation-user-short")} value={operator} />
        <InfoRow icon={noteIcon} label={t("prev-surgery.note-section")} value={note} />
        <InfoRow icon={dateIcon} label={t("prev-surgery.surgery-date")} value={surgeryDate} />
        <InfoRow icon={dateIcon} label={t("prev-surgery.institution")} value={institution} />
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
          }
        }}
        title={t("prev-surgery.delete-confirm-title")}
        content={t("prev-surgery.delete-confirm-content")}
        successMessage={t("prev-surgery.delete-success")}
        errorMessage={t("prev-surgery.delete-error")}
        hideSnackbar={true}
      />
    </>
  );
};

interface PrevSurgeriesListCardProps {
  data?: McGeneralDiseaseItem[];
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const PrevSurgeriesListCard: FunctionComponent<PrevSurgeriesListCardProps> = ({
  data = [],
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const items: SurgeryCardProps[] = data.map((item) => ({
    name: item.name || "",
    operationDate: formatDateTime(item.ctime, "dd.MM.yyyy HH:mm") || "",
    operator: item.cuser || "",
    note: item.note || item.privatenotes || "",
    surgeryDate: formatDateTime(item.pdate, "dd.MM.yyyy HH:mm") || "",
    institution: item.opassname || "",
    id: item.id,
    originalItem: item,
    onDelete,
    onEdit: () => onEdit?.(item),
    onDeleteSuccess,
    onDeleteError,
  }));

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "12px" }}>
      {items.map((item, index) => (
        <SurgeryCard key={`${item.name}-${item.surgeryDate}-${index}`} {...item} />
      ))}
    </Box>
  );
};

export default PrevSurgeriesListCard;


